import {
  addDoc, collection, doc, getDoc,
  getFirestore, onSnapshot, updateDoc,
  serverTimestamp, deleteDoc, setDoc,
  Timestamp, query, orderBy, limit, QueryConstraint
} from "@firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytes } from "@firebase/storage";
import { useEffect, useState } from "react";
import app from "../firebase";
import { parse as toMarkdown } from 'marked';
import { sanitize as sanitizeHTML } from 'dompurify';

export type NewsInfo = {
  id: string;
  title: string;
  body: string;
  coverPath: string;
  timestamp: Timestamp;
};

export function previewNewsAsHTML(newsBody: string): string {
  return sanitizeHTML(toMarkdown(newsBody));
}

export async function uploadNews(title: string, cover: File, body: string) {
  const docRef = await addDoc(collection(getFirestore(app), '/news'), {
    title: title,
    body: body,
    timestamp: serverTimestamp()
  });

  const ext = cover.type.substring(cover.type.lastIndexOf('/') + 1);
  const coverPath = `/news/${ docRef.id }/cover.${ ext }`;

  await uploadBytes(ref(getStorage(app), coverPath), cover);
  await updateDoc(doc(getFirestore(app), `/news/${ docRef.id }`), {
    coverPath: coverPath
  });

  return docRef.id;
}

export async function archiveNews(id: string) {
  const newsSnapshot = await getDoc(doc(getFirestore(app), `/news/${ id }`));

  await deleteDoc(newsSnapshot.ref);
  await setDoc(doc(getFirestore(app), `/news-archive/${ id }`), {
    body: newsSnapshot.get('body'),
    coverPath: newsSnapshot.get('coverPath'),
    timestamp: newsSnapshot.get('timestamp'),
    title: newsSnapshot.get('body'),
  } as Partial<NewsInfo>);
}

export async function editNews(id: string, title: string, body: string) {
  await updateDoc(doc(getFirestore(app), `/news/${ id }`), {
    body: body,
    title: title
  } as Partial<NewsInfo>);
}

export async function deleteNews(id: string) {
  await deleteDoc(doc(getFirestore(app), `/news/${ id }`));
}

export async function downloadNewsCoverPhoto(id: string, path?: string): Promise<File> {
  const actualPath = path || (await getDoc(
    doc(getFirestore(app), `/news/${ id }`)
  )).get('coverPhoto') as string;
  const url = await getDownloadURL(ref(getStorage(app), actualPath));
  return new File([ await (await fetch(url)).blob() ], `cover-${ id }`);
}

export function useNewsList(maxAmount?: number): NewsInfo[] {
  const [ newsList, setNewsList ] = useState<NewsInfo[]>([]);


  useEffect(() => {
    const c = collection(getFirestore(app), 'news');
    const qc: QueryConstraint[] = [
      orderBy('timestamp', 'desc'),
      !maxAmount ? null : limit(maxAmount)
    ].filter((q) => !!q) as QueryConstraint[];

    const unsub = onSnapshot(query(c, ...qc), (newCollection) => {
      setNewsList(newCollection.docs.map((d) => {
        return {
          body: d.get('body'),
          coverPath: d.get('coverPath'),
          id: d.id,
          timestamp: d.get('timestamp'),
          title: d.get('title'),
        };
      }));
    });

    return () => unsub();
  }, [ maxAmount ]);

  return newsList;
}

export function useSpecificNews(id: string): [ NewsInfo | undefined, boolean ] {
  const [ news, setNews ] = useState<NewsInfo>();
  const [ loading, setLoading ] = useState(true);

  useEffect(() => {
    getDoc(doc(getFirestore(app), `/news/${ id }`)).then((d) => {
      setNews({
        body: d.get('body'),
        coverPath: d.get('coverPath'),
        id: d.id,
        timestamp: d.get('timestamp'),
        title: d.get('title')
      });
    }).catch((err) => {
      console.error(err);
    }).finally(() => {
      setLoading(false);
    });
  }, [ id ]);

  return [ news, loading ];
}
